// src/Pages/ManageEventWizard.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { styles, POLL_MS } from '../Components/EventWizard/lib/ui';
import { sanitizeMpd, generateMpdString } from '../Components/EventWizard/lib/mpd';
import { useEventWizardApi } from '../hooks/useEventWizardApi';
import { useStatuses } from '../hooks/useStatuses';
import { authFetch } from '../utils/authFetch';

import MinimalDashboard from '../Components/EventWizard/MinimalDashboard';
import FfmpegPresetsPanel from '../Components/EventWizard/FfmpegPresetsPanel';
import '../Pages/CSS/StudioMode.css';

export default function ManageEventWizard({ backendUrl, resetKey }) {
  const [eventName, setEventName] = useState('');
  const [cams, setCams] = useState('1');
  const [videoInstanceType, setVideoInstanceType] = useState('g6.2xlarge');
  const [videoInstanceTypeCustom, setVideoInstanceTypeCustom] = useState('');
  // Which S3 controller the instances download at boot: 'stable' (production)
  // or 'dev' (in-progress controller work, e.g. the LiveKit slot).
  // Remembered across reloads: during a dev-testing phase, silently resetting
  // to stable on every page load meant launching the wrong controller without
  // noticing. Anyone who never picked still gets the safe default.
  const [controllerChannel, setControllerChannel] = useState(
    () => localStorage.getItem('ew.controllerChannel') === 'dev' ? 'dev' : 'stable'
  );
  useEffect(() => {
    localStorage.setItem('ew.controllerChannel', controllerChannel);
  }, [controllerChannel]);
  // Launch region — operator-selected and STRICT (no automatic fallback).
  // Default us-east-1: the web server lives there and multicam sync measures
  // segment arrivals at it, so cams must share its region. Picking another
  // region is a conscious choice (sync against the web server will need
  // manual offset care unless the web server moves too).
  const [launchRegion, setLaunchRegion] = useState(
    () => localStorage.getItem('ew.launchRegion') || 'us-east-1'
  );
  useEffect(() => {
    localStorage.setItem('ew.launchRegion', launchRegion);
  }, [launchRegion]);
  const [instances, setInstances] = useState([]);
  const [message, setMessage] = useState('');

  // (advanced view is now default for EventWizard; MinimalDashboard only in studio mode)

  const [rtmpOk, setRtmpOk] = useState({});
  const [ingestOk, setIngestOk] = useState({});
  const [ingestKbps, setIngestKbps] = useState({});
  const [ffmpegStat, setFfmpegStat] = useState({});
  const [speedMap, setSpeedMap] = useState({});
  const [sourceOverrideMap, setSourceOverrideMap] = useState({});

  const [mpdContent, setMpdContent] = useState({});
  const [eventStartTimes, setEventStartTimes] = useState({});
  const [eventPublishTimes, setEventPublishTimes] = useState({});
  const [showManifest, setShowManifest] = useState({});
  const [manifestText, setManifestText] = useState({});
  const [manifestText8K, setManifestText8K] = useState({});
  const [manifestTextFull, setManifestTextFull] = useState({});
  const [manifestMeta, setManifestMeta] = useState({});
  const [dashSettingsContent, setDashSettingsContent] = useState({});

  const [cmdFromDb, setCmdFromDb] = useState({});

  // presets
  const [presets, setPresets] = useState([]);
  const presetsLoadedRef = useRef(false);
  const [autoAppliedDefault, setAutoAppliedDefault] = useState({}); // { [instanceId]: true }
  const [showPresetsPanel, setShowPresetsPanel] = useState(false);

  // NEW: Pending launches to display immediately in MinimalDashboard
  const [pendingLaunchesByEvent, setPendingLaunchesByEvent] = useState({});
  const addPending = useCallback((evt, items) => {
    setPendingLaunchesByEvent(prev => ({
      ...prev,
      [evt]: [
        ...(prev[evt] || []),
        ...items.map(it => ({
          ...it,
          tempId: it.tempId || `${evt}:${Date.now()}:${Math.random()}`
        }))
      ]
    }));
  }, []);
  const reconcilePendingWithInstances = useCallback((evt, list) => {
    setPendingLaunchesByEvent(prev => {
      const pend = prev[evt] || [];
      if (!pend.length) return prev;
      const byNameRole = new Set(list.map(i => `${(i.camName||'').toLowerCase()}|${(i.role||'').toLowerCase()}`));
      const byId = new Set(list.map(i => i.instanceId));
      const kept = pend.filter(p => {
		// Eliminar cualquier fila pendiente de tipo master con camName distinto de 'master'
        if ((p.role || '').toLowerCase() === 'master' && (p.camName || '').toLowerCase() !== 'master') return false;
        const key = `${(p.camName||'').toLowerCase()}|${(p.role||'').toLowerCase()}`;
        const matchedByName = p.camName && byNameRole.has(key);
        const matchedById   = p.instanceId && byId.has(p.instanceId);
        return !(matchedByName || matchedById);
      });
      return kept.length === pend.length ? prev : { ...prev, [evt]: kept };
    });
  }, []);

  const api = useEventWizardApi(backendUrl);
  // Backend answered 410 for an instance: it was terminated but our list is
  // stale — reload it so the dead rows (and their 2s polling) disappear.
  // useCallback([]) because refreshStatuses lists this in its deps: an inline
  // arrow would give it a new identity every render and tear down the status
  // poll timer before it could ever fire (the bug documented in useStatuses).
  const onInstanceGone = useCallback(() => fetchFullRef.current(), []);

  const { refreshStatuses } = useStatuses({
    setRtmpOk, setIngestOk, setFfmpegStat, setSpeedMap, setIngestKbps, setSourceOverrideMap, checkRtmp: api.checkRtmp,
    onInstanceGone,
  });

  // Memoised because MinimalDashboard lists `grouped` in several effect deps: a new
  // object on every render restarted those effects' timers before they could fire.
  const grouped = useMemo(() => instances.reduce((acc, inst) => {
    (acc[inst.eventName || 'unknown'] ||= []).push(inst);
    return acc;
  }, {}), [instances]);

  const fetchFullRef = useRef(async () => {});
  const reloadPresets = useCallback(async () => {
    try {
      const r = await api.listPresets();
      const arr = r?.data?.presets || [];
      setPresets(arr);
    } catch (e) {
      setMessage(`Load presets error: ${e.message}`);
    }
  }, [api, setMessage]);

  useEffect(() => {
    if (presetsLoadedRef.current) return;
    presetsLoadedRef.current = true;
    reloadPresets();
  }, [reloadPresets]);

  const saveMpdToDb = useCallback(async (evt, xml) => {
    try {
      const safe = sanitizeMpd(xml, evt);
      await api.saveMpd(evt, safe);
      setMessage(`MPD saved to DB for "${evt}"`);
    } catch (err) {
      setMessage(`Failed to save MPD: ${err.message}`);
    }
  }, [api, setMessage]);

const fetchManifestForEvent = useCallback(async (evt, masterInst) => {
	let txt = '', source = '', url = '';
	let errorMsg = '';
	// 1) Try NODE (proxy to :3000)
	try {
	if (masterInst?.instanceId) {
		const { data } = await api.getManifest(masterInst.instanceId, evt);
      txt = typeof data === 'string' ? data : (data?.mpd || '');
      if (txt) source = 'node';
    }
  } catch (err) {
    errorMsg = `Backend proxy error: ${err?.message || String(err)}`;
  }
  // 2) Fallback to CloudFront
  if (!txt) {
    try {
      url = `https://d10tner3hocpa9.cloudfront.net/${encodeURIComponent(evt)}/master/manifest.mpd`;
      const res = await fetch(url, { cache: 'no-store' });
      if (res.ok) { txt = await res.text(); source = 'cloudfront'; }
    } catch (err) {
      errorMsg = `CloudFront fetch error: ${err?.message || String(err)}`;
    }
  }
  // 3) LAST resort: DB (for debugging only — not the one we show normally)
  let dbData = null;
  if (!txt) {
    try {
      const { data } = await api.getEvent(evt);
      dbData = data;
      if (data?.success && data?.mpd) { txt = data.mpd; source = 'db'; }
    } catch {}
  }
  if (!txt) {
    setManifestText(v => ({ ...v, [evt]: `(Could not load manifest.mpd)\n${errorMsg}` }));
    setManifestMeta(v => ({ ...v, [evt]: { source:'none', url:'' } }));
    return;
  }
  const cleaned = sanitizeMpd(txt, evt);
  setManifestText(v => ({ ...v, [evt]: cleaned || txt }));
  setManifestMeta(v => ({ ...v, [evt]: { source, url, origin: source } }));
  setMpdContent(v => v[evt] ? v : ({ ...v, [evt]: cleaned || txt }));

  // Fetch 8K and Full variants (CloudFront first, then DB fallback)
  const cfBase = `https://d10tner3hocpa9.cloudfront.net/${encodeURIComponent(evt)}/master`;
  // 8K variant
  let txt8K = '';
  try {
    const res8K = await fetch(`${cfBase}/manifest_8K.mpd`, { cache: 'no-store' });
    if (res8K.ok) txt8K = await res8K.text();
  } catch {}
  if (!txt8K) {
    if (!dbData) { try { dbData = (await api.getEvent(evt)).data; } catch {} }
    if (dbData?.mpd8K) txt8K = dbData.mpd8K;
  }
  setManifestText8K(v => ({ ...v, [evt]: txt8K }));

  // Full variant
  let txtFull = '';
  try {
    const resFull = await fetch(`${cfBase}/manifestFull.mpd`, { cache: 'no-store' });
    if (resFull.ok) txtFull = await resFull.text();
  } catch {}
  if (!txtFull) {
    if (!dbData) { try { dbData = (await api.getEvent(evt)).data; } catch {} }
    if (dbData?.mpdFull) txtFull = dbData.mpdFull;
  }
  setManifestTextFull(v => ({ ...v, [evt]: txtFull }));
}, [api]);

  const uploadMpdToNode = useCallback(async (evt, mpdXml, master, mpd8K, mpdFull) => {
    try {
      if (!master) throw new Error('Master instance not found for this event.');
      const safe = sanitizeMpd(mpdXml, evt);
      const safe8K = mpd8K ? sanitizeMpd(mpd8K, evt) : undefined;
      const safeFull = mpdFull ? sanitizeMpd(mpdFull, evt) : undefined;
	await api.setManifest(master.instanceId, safe, evt, safe8K, safeFull);
	setMessage(`MPD variants uploaded to ${master.publicIp}`);
    } catch (err) {
      setMessage(`Failed to upload MPD: ${err.message}`);
    }
  }, [api, setMessage]);

  const generateMpdPreview = (evt, list) => {
    const now = new Date();
    const publish = eventPublishTimes[evt] || now.toISOString();
    const avail   = eventStartTimes[evt]   || new Date(now.getTime() - 10000).toISOString();
    const xml = generateMpdString(evt, list, publish, avail);
    setMpdContent(prev => ({ ...prev, [evt]: xml }));
    saveMpdToDb(evt, xml);
  };

  const handleGenerateAndUpload = async (evt, list, master, camMpdData = [], { resetAst = false, forcePtoZero = false } = {}) => {
    const now = new Date();
    let publish = eventPublishTimes[evt] || now.toISOString();
    let avail   = eventStartTimes[evt]   || new Date(now.getTime() - 10000).toISOString();
    try {
      const { data } = await api.getEvent(evt);
      if (data.success) {
        if (data.publishTime) publish = data.publishTime;
        if (data.availabilityStartTime && !resetAst) avail = data.availabilityStartTime;
        setEventStartTimes(v => ({ ...v, [evt]: resetAst ? null : avail }));
        setEventPublishTimes(v => ({ ...v, [evt]: publish }));
      }
    } catch {}
    // Send camMpdData + masterInstanceId to backend — backend rebuilds AND pushes to master directly
    try {
      const masterInstanceId = master?.instanceId || null;
      const res = await authFetch(`${backendUrl}/event-wizard/event/${evt}/mpd-rebuild`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ camMpdData, masterInstanceId, resetAst, forcePtoZero })
      });
      const json = await res.json();
      if (json.success && json.mpd) {
        setMpdContent(v => ({ ...v, [evt]: json.mpd }));
        // DB is already saved by backend; refresh manifest view
        await fetchManifestForEvent(evt, master);
        const pushInfo = json.pushResult === 'ok' ? ' + pushed to master' : (json.pushResult ? ` (push: ${json.pushResult})` : '');
        setMessage(`MPD rebuilt and saved${pushInfo}`);
      } else {
        setMessage(`Failed to rebuild MPD: ${json.message || 'Unknown error'}`);
      }
    } catch (err) {
      setMessage(`Failed to rebuild MPD: ${err.message}`);
    }
  };

// NEW: upload to the node first (source of truth), then save same XML in DB, then refresh the server view
const saveAndPushMpd = useCallback(
  async (evt, xml, masterInst, mpd8K, mpdFull) => {
    if (!masterInst) throw new Error('Master instance not found for this event.');

    // 1) Push all manifest variants to the master node (server copy)
    await uploadMpdToNode(evt, xml, masterInst, mpd8K, mpdFull);

    // 2) Persist main XML in DB (backup)
    await saveMpdToDb(evt, xml);

    // 3) Refresh from the server so the UI shows what's actually live
    await fetchManifestForEvent(evt, masterInst);
  },
  [uploadMpdToNode, saveMpdToDb, fetchManifestForEvent]
);

  // Optional: auto-apply a DB default to cams with no command yet
  const applyDefaultPresetIfNeeded = useCallback(async (instList, cmdMap) => {
    const def = (await (async () => {
      try {
        const r = await api.listPresets();
        const arr = r?.data?.presets || [];
        return arr.find(p => p.isDefault) || null;
      } catch { return null; }
    })());
    if (!def) return;
    const tasks = [];
    for (const inst of instList) {
      if (!inst.instanceId) continue;
      const isCam = (inst.role || '').toLowerCase() !== 'master';
      const hasCmd = !!cmdMap[inst.instanceId];
      const already = autoAppliedDefault[inst.instanceId];
      if (!isCam || hasCmd || already) continue;

      tasks.push((async () => {
        try {
          await api.saveEventCmd(inst.eventName, { instanceId: inst.instanceId, command: def.command });
          await api.updateNodeScript(inst.instanceId, def.command);
          setMessage(`Applied default preset "${def.name}" to ${inst.camName}`);
          setAutoAppliedDefault(prev => ({ ...prev, [inst.instanceId]: true }));
          setCmdFromDb(prev => ({ ...prev, [inst.instanceId]: def.command }));
        } catch (e) {
          setMessage(`Auto-apply default failed for ${inst.camName}: ${e.message}`);
        }
      })());
    }
    if (tasks.length) await Promise.all(tasks);
  }, [api, autoAppliedDefault, setMessage]);

  const fetchFull = useCallback(async () => {
    setMessage('');
    try {
      const list = await api.listInstances();
      setInstances(list);

      // Reconcile pending placeholders with real instances
      const groupedNow = list.reduce((acc, i) => {
        (acc[i.eventName || 'unknown'] ||= []).push(i);
        return acc;
      }, {});
      Object.entries(groupedNow).forEach(([evt, arr]) => reconcilePendingWithInstances(evt, arr));

      const events = [...new Set(list.map(i => i.eventName))];
      const tmpCmdMap = {};
      await Promise.all(events.map(async evt => {
        try {
          const r = await api.getEvent(evt);
          const d = r.data || {};
          if (d.availabilityStartTime) setEventStartTimes(v => ({ ...v, [evt]: d.availabilityStartTime }));
          if (d.publishTime)           setEventPublishTimes(v => ({ ...v, [evt]: d.publishTime }));
          if (d.mpd && !mpdContent[evt]) setMpdContent(v => ({ ...v, [evt]: sanitizeMpd(d.mpd, evt) }));
          if (d.dashSettings && !dashSettingsContent[evt]) setDashSettingsContent(v => ({ ...v, [evt]: d.dashSettings }));
          if (Array.isArray(d.commands)) {
            for (const c of d.commands) {
              if (c.instanceId && typeof c.command === 'string') {
                tmpCmdMap[c.instanceId] = c.command;
              }
            }
          }
        } catch {}
      }));

      setCmdFromDb(prev => ({ ...prev, ...tmpCmdMap }));

      await applyDefaultPresetIfNeeded(list, tmpCmdMap);

      await refreshStatuses(list);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
  }, [api, refreshStatuses, mpdContent, applyDefaultPresetIfNeeded, setMessage, reconcilePendingWithInstances]);

  useEffect(() => { fetchFullRef.current = fetchFull; }, [fetchFull]);
  useEffect(() => { fetchFullRef.current(); }, [resetKey]);

  // Serialized auto-refresh (no overlapping polls)
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        if (document.visibilityState !== 'visible') return scheduleNext();
        await refreshStatuses(instances);
      } catch {}
      scheduleNext();
    };
    const scheduleNext = () => { if (!cancelled) setTimeout(tick, POLL_MS); };
    scheduleNext();

    // Browsers throttle background-tab timers down to ~1/min, so after
    // returning to the tab the chained setTimeout above can take up to a
    // minute to fire — badges look frozen at their pre-switch state and a
    // page reload "fixes" it. Refresh immediately on becoming visible
    // instead; the seq guard in useStatuses makes the overlap with the
    // scheduled tick harmless.
    const onVisible = () => {
      if (!cancelled && document.visibilityState === 'visible') {
        refreshStatuses(instances).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [instances, refreshStatuses]);

  const [loading, setLoading] = useState(false);
  // Minimal template MPD builder (no AdaptationSets)
  const buildTemplateMpd = useCallback((evt, publishIso, availIso) => {
    // Reasonable live defaults; keep it minimal and valid
    return `<?xml version="1.0" encoding="UTF-8"?>
<MPD xmlns="urn:mpeg:DASH:schema:MPD:2011"
     profiles="urn:mpeg:dash:profile:isoff-live:2011"
     type="dynamic"
    minimumUpdatePeriod="PT2S"
     timeShiftBufferDepth="PT60S"
     suggestedPresentationDelay="PT6S"
     availabilityStartTime="${availIso}"
     publishTime="${publishIso}">
  <Period id="p0" start="PT0S"></Period>
</MPD>`;
  }, []);

  const resetEventInDb = useCallback(async (evt) => {
    try {
      const { data } = await api.getEvent(evt);
      if (data?.success) {
        await api.deleteEvent(evt);
        setMessage(`Event "${evt}" existed — cleaned from DB before creating.`);
        return true;
      }
    } catch {}
    return false;
  }, [api, setMessage]);

  const waitForMaster = useCallback(async (evt, { attempts = 12, delayMs = 2500 } = {}) => {
    for (let i = 1; i <= attempts; i++) {
      const list = await api.listInstances();
      const hasMaster = list.some(inst => inst.eventName === evt && (inst.role || '').toLowerCase() === 'master');
      if (hasMaster) return true;
      await new Promise(res => setTimeout(res, delayMs));
    }
    return false;
  }, [api]);

  const handleLaunch = async () => {
    setMessage('');
    if (!eventName) return setMessage('Invalid launch parameters');
    try {
      setLoading(true);
      await resetEventInDb(eventName);

      // Get the actual input values from the form
      const videoInput = document.querySelector('input[list="video-instance-types"]')?.value || videoInstanceType;

      // Cams launch in their own try: a capacity failure here must NOT skip
      // the master launch below — the event skeleton always comes up, and
      // cams get retried as capacity appears (pinned to the region by the
      // backend). This was the historical half-born-event bug.
      try {
        const r = await api.launch(eventName, cams, 0, videoInput, videoInput, controllerChannel, launchRegion);
        if (!r.data.success) throw new Error(r.data.message);
        const created = Array.isArray(r.data.instances) ? r.data.instances : [];

        // add as pending
        addPending(eventName, created.map(i => ({
          camName: i.camName || i.name,
          role: (i.role || 'video').toLowerCase(),
          instanceId: i.instanceId
        })));

        // NUEVO: mostrar instancias en tiempo real
        setInstances(prev => [...prev, ...created]);

        setMessage(`Launched ${created.length} instance(s) (cams/audios)` +
          (r.data.capacityShortfall ? ` — ${r.data.message}` : '…'));
      } catch (e) {
        setMessage(`Cams: ${e.response?.data?.message || e.message} — launching master anyway; retry cams when there is capacity.`);
      }

      try {
        const rm = await api.launchMaster(eventName, launchRegion);
        if (rm.data?.success && rm.data.instance) {
          const m = rm.data.instance;

          // pending master (last to appear)
          addPending(eventName, [{ camName: 'master', role: 'master', instanceId: m.instanceId }]);

          // NUEVO: mostrar master en tiempo real
          setInstances(prev => [...prev, m]);

          setMessage('Master instance launched.');
        } else {
          setMessage(rm.data?.message || 'Master may already exist.');
        }
      } catch (e) { setMessage(`Master launch warning: ${e.message}`); }

      const ok = await waitForMaster(eventName);
      if (!ok) setMessage(prev => (prev ? `${prev} | ` : '') + 'Master not visible yet (timeout).');

      await fetchFullRef.current();
      setMessage('✅ All instances deployed and ready.');
    } catch (err) {
      setMessage(`Launch error: ${err.message}`);
    } finally { setLoading(false); }
  };

  const [studioMode, setStudioMode] = useState(true);
  const [studioFadein, setStudioFadein] = useState(false); // NEW: for fade-in animation

  const handleStudioToggle = () => {
    if (!studioMode) {
      setStudioFadein(true);
      setStudioMode(true);
      setTimeout(() => setStudioFadein(false), 500); // match animation duration
    } else {
      setStudioMode(false);
    }
  };

function Counter({ label, value, setValue, min = 0, max = 99, step = 1, theme = 'default' }) {
  const SQUARE = 36;
  const VALUE_W = 64;
  const v = Number.isFinite(+value) ? +value : 0;
  const clamp = (n) => Math.max(min, Math.min(max, n));
  const set = (n) => setValue(String(clamp(n)));

  const dec = () => set(v - step);
  const inc = () => set(v + step);

  const isStudio = theme === 'studio';

  const wrapperStyle = {
    display: 'grid',
    gridTemplateColumns: `${SQUARE}px ${VALUE_W}px ${SQUARE}px`,
    alignItems: 'center',
    height: SQUARE,
    borderRadius: 8,
    boxSizing: 'border-box',
    overflow: 'hidden',
    // background + border per theme
    background: isStudio ? '#2b2b2b' : '#fff',
    border: isStudio ? '1px solid #444' : '1px solid #cfcfcf',
  };

  const btnBase = {
    width: SQUARE,
    height: SQUARE,
    padding: 0,
    margin: 0,
    border: 'none',
    boxSizing: 'border-box',
    cursor: 'pointer',
    fontSize: 18,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // colors per theme
    background: isStudio ? 'linear-gradient(90deg, #9a51e2, #7a3fb7)' : '#eeeeee',
    color: isStudio ? '#fff' : '#333',
  };

  const valueStyle = {
    width: VALUE_W,
    height: SQUARE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: 16,
    boxSizing: 'border-box',
    // colors per theme
    background: isStudio ? '#222' : '#fff',
    color: isStudio ? '#fff' : '#111',
    borderLeft: isStudio ? 'none' : '1px solid #cfcfcf',
    borderRight: isStudio ? 'none' : '1px solid #cfcfcf',
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <label style={{ minWidth: 70, color: '#fff' }}>{label}:</label>

      <div role="group" aria-label={`${label} counter`} style={wrapperStyle}>
        <button
          type="button"
          onClick={dec}
          aria-label={`Decrease ${label}`}
          style={{ ...btnBase, borderRadius: '8px 0 0 8px' }}
        >
          −
        </button>

        <div aria-live="polite" style={valueStyle}>
          {v}
        </div>

        <button
          type="button"
          onClick={inc}
          aria-label={`Increase ${label}`}
          style={{ ...btnBase, borderRadius: '0 8px 8px 0' }}
        >
          +
        </button>
      </div>
    </div>
  );
}

const sanitizeEventKey = (s) =>
  String(s || '')
    .replace(/\s+/g, '-')           // spaces → hyphens
    .replace(/[^a-zA-Z0-9_-]/g, '') // allow letters, numbers, _ and -
    .replace(/-{2,}/g, '-')         // collapse multiple hyphens
    .replace(/_{2,}/g, '_')         // collapse multiple underscores
    .toLowerCase();
  return (
    <div
      className={
        studioMode
          ? `studio-mode${studioFadein ? ' studio-fadein' : ''}`
          : ''
      }
      style={studioMode ? undefined : { ...styles.wrapper }}
    >
      <h2>Broadcast Control</h2>
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 16, marginBottom: 16, marginRight: studioMode ? 32 : undefined }}>
        {/* FFmpeg Presets Button - Only show when NOT in studio mode */}
        {!studioMode && (
          <button
            onClick={() => setShowPresetsPanel(true)}
            style={{
              background: 'linear-gradient(90deg, #9a51e2 60%, #7a3fb7 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 20px',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 2px 8px rgba(154,81,226,0.15)',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(90deg, #7a3fb7 60%, #9a51e2 100%)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(154,81,226,0.25)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(90deg, #9a51e2 60%, #7a3fb7 100%)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(154,81,226,0.15)';
            }}
          >
            <span style={{ fontSize: 18 }}>⚙️</span>
            FFmpeg Presets
          </button>
        )}

        {/* Switch visual tipo bola ON/OFF para modo Studio */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
          <span
            style={{
              fontWeight: 'bold',
              fontSize: 18,
              color: '#fff',
              transition: 'color 0.2s, font-size 0.2s',
              letterSpacing: 0.5,
            }}
          >
            Studio mode
          </span>
          <button
            onClick={() => {
              if (!studioMode) {
                setStudioFadein(true);
                setStudioMode(true);
                setTimeout(() => setStudioFadein(false), 500);
              } else {
                setStudioMode(false);
              }
            }}
            style={{
              width: 52,
              height: 28,
              borderRadius: 16,
              border: 'none',
              background: studioMode ? '#9c27b0' : '#ccc',
              position: 'relative',
              cursor: 'pointer',
              transition: 'background 0.2s',
              outline: 'none',
              boxShadow: studioMode ? '0 0 0 2px #9c27b033' : 'none',
              display: 'inline-block',
              padding: 0,
            }}
            aria-label="Toggle Studio mode"
          >
            <span
              style={{
                position: 'absolute',
                left: studioMode ? 26 : 4,
                top: 4,
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: '#fff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                transition: 'left 0.2s',
                border: '1px solid #bbb',
              }}
            />
          </button>
        </div>
      </div>
      {/* Panel principal: oculta opciones avanzadas en modo Studio */}
      <div>
       <div style={styles.row}>
		  <label style={{ minWidth:80, color: studioMode ? '#fff' : '#fff' }}>Event Name:</label>
		  <input
			value={eventName}
			onChange={e => setEventName(sanitizeEventKey(e.target.value))}
			inputMode="latin"
			pattern="[a-z0-9_-]+"
			title="Only lowercase letters, numbers, - and _"
			style={{
			  background: '#181828',
			  color: '#fff',
			  border: '1px solid #555',
			  borderRadius: 6,
			  padding: '8px 12px',
			  fontSize: 15
			}}
		  />
		</div>
        <div style={{ ...styles.row, gap:16, flexWrap:'wrap' }}>
		  <Counter label="Cameras" value={cams} setValue={setCams} min={0} max={99}
				   theme="studio" />
		  <div>
		    <label style={{ display: 'block', color: '#fff', marginBottom: 6, fontSize: 13 }}>Controller:</label>
		    <select
		      value={controllerChannel}
		      onChange={e => setControllerChannel(e.target.value)}
		      title="stable = production controller. dev = in-progress controller (needed for the LiveKit slot) — never affects instances launched as stable."
		      style={{ padding: '8px 12px', background: '#222', color: controllerChannel === 'dev' ? '#ff9800' : '#fff', border: '1px solid #444', borderRadius: 6, fontSize: 13 }}
		    >
		      <option value="stable">stable</option>
		      <option value="dev">dev</option>
		    </select>
		  </div>
		  <div>
		    <label style={{ display: 'block', color: '#fff', marginBottom: 6, fontSize: 13 }}>Region:</label>
		    <select
		      value={launchRegion}
		      onChange={e => setLaunchRegion(e.target.value)}
		      title="Strict: cams launch ONLY here (no automatic fallback). Virginia is where the web server lives — picking another region degrades multicam sync unless the web server moves too."
		      style={{ padding: '8px 12px', background: '#222', color: launchRegion === 'us-east-1' ? '#fff' : '#ff9800', border: '1px solid #444', borderRadius: 6, fontSize: 13 }}
		    >
		      <option value="us-east-1">Virginia (us-east-1)</option>
		      <option value="eu-south-2">Spain (eu-south-2)</option>
		      <option value="eu-west-3">Paris (eu-west-3)</option>
		      <option value="eu-central-1">Frankfurt (eu-central-1)</option>
		    </select>
		  </div>
		</div>

		{/* Instance Type Selectors - Hidden in Studio Mode */}
		{!studioMode && (
		  <div style={{ marginTop: 16, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
		    {/* Video Instance Type */}
		    <div>
		      <label style={{ display: 'block', minWidth: 70, color: '#fff', marginBottom: 6 }}>Video Instance Type:</label>
		      <input
		        type="text"
		        list="video-instance-types"
		        placeholder="g6.2xlarge"
		        value={videoInstanceTypeCustom || videoInstanceType}
		        onChange={(e) => {
		          setVideoInstanceTypeCustom(e.target.value);
		          if (!e.target.value) setVideoInstanceType('g6.2xlarge');
		        }}
		        style={{
		          padding: '8px 12px',
		          background: '#222',
		          color: '#fff',
		          border: '1px solid #444',
		          borderRadius: 6,
		          fontSize: 13,
		          minWidth: 140,
		          boxSizing: 'border-box'
		        }}
		        title="Select or type custom AWS EC2 instance type"
		      />
		      <datalist id="video-instance-types">
		        <option value="g6.xlarge" />
		        <option value="g6.2xlarge" />
		        <option value="g6.4xlarge" />
		        <option value="g6.8xlarge" />
		        <option value="g6.12xlarge" />
		        <option value="g6.16xlarge" />
		        <option value="g6.24xlarge" />
		        <option value="g6.48xlarge" />
		        <option value="g5.xlarge" />
		        <option value="g5.2xlarge" />
		        <option value="g5.4xlarge" />
		        <option value="g5.8xlarge" />
		        <option value="g5.12xlarge" />
		        <option value="g5.16xlarge" />
		        <option value="g5.24xlarge" />
		        <option value="g5.48xlarge" />
		        <option value="t3.medium" />
		        <option value="t3.large" />
		        <option value="t3.xlarge" />
		        <option value="t3.2xlarge" />
		        <option value="c7i.large" />
		        <option value="c7i.xlarge" />
		        <option value="c7i.2xlarge" />
		        <option value="c7i.4xlarge" />
		        <option value="c7i.8xlarge" />
		        <option value="c7i.12xlarge" />
		        <option value="c7i.16xlarge" />
		        <option value="c7i.24xlarge" />
		        <option value="c7i.metal" />
		        <option value="c6i.large" />
		        <option value="c6i.xlarge" />
		        <option value="c6i.2xlarge" />
		        <option value="c6i.4xlarge" />
		        <option value="c6i.8xlarge" />
		        <option value="c6i.12xlarge" />
		        <option value="c6i.16xlarge" />
		        <option value="c6i.24xlarge" />
		        <option value="c6i.metal" />
		        <option value="c5.large" />
		        <option value="c5.xlarge" />
		        <option value="c5.2xlarge" />
		        <option value="c5.4xlarge" />
		        <option value="c5.9xlarge" />
		        <option value="c5.12xlarge" />
		        <option value="c5.18xlarge" />
		        <option value="c5.24xlarge" />
		        <option value="m7i.large" />
		        <option value="m7i.xlarge" />
		        <option value="m7i.2xlarge" />
		        <option value="m7i.4xlarge" />
		        <option value="m7i.8xlarge" />
		        <option value="m7i.12xlarge" />
		        <option value="m7i.16xlarge" />
		        <option value="m7i.24xlarge" />
		        <option value="m7i.metal" />
		        <option value="m6i.large" />
		        <option value="m6i.xlarge" />
		        <option value="m6i.2xlarge" />
		        <option value="m6i.4xlarge" />
		        <option value="m6i.8xlarge" />
		        <option value="m6i.12xlarge" />
		        <option value="m6i.16xlarge" />
		        <option value="m6i.24xlarge" />
		        <option value="m6i.metal" />
		        <option value="m5.large" />
		        <option value="m5.xlarge" />
		        <option value="m5.2xlarge" />
		        <option value="m5.4xlarge" />
		        <option value="m5.8xlarge" />
		        <option value="m5.12xlarge" />
		        <option value="m5.16xlarge" />
		        <option value="m5.24xlarge" />
		        <option value="r7i.large" />
		        <option value="r7i.xlarge" />
		        <option value="r7i.2xlarge" />
		        <option value="r7i.4xlarge" />
		        <option value="r7i.8xlarge" />
		        <option value="r7i.12xlarge" />
		        <option value="r7i.16xlarge" />
		        <option value="r7i.24xlarge" />
		        <option value="r7i.metal" />
		        <option value="r6i.large" />
		        <option value="r6i.xlarge" />
		        <option value="r6i.2xlarge" />
		        <option value="r6i.4xlarge" />
		        <option value="r6i.8xlarge" />
		        <option value="r6i.12xlarge" />
		        <option value="r6i.16xlarge" />
		        <option value="r6i.24xlarge" />
		        <option value="r6i.metal" />
		        <option value="r5.large" />
		        <option value="r5.xlarge" />
		        <option value="r5.2xlarge" />
		        <option value="r5.4xlarge" />
		        <option value="r5.8xlarge" />
		        <option value="r5.12xlarge" />
		        <option value="r5.16xlarge" />
		        <option value="r5.24xlarge" />
		        <option value="x1e.xlarge" />
		        <option value="x1e.2xlarge" />
		        <option value="x1e.4xlarge" />
		        <option value="x1e.8xlarge" />
		        <option value="x1e.16xlarge" />
		        <option value="x1e.32xlarge" />
		        <option value="z1d.large" />
		        <option value="z1d.xlarge" />
		        <option value="z1d.2xlarge" />
		        <option value="z1d.3xlarge" />
		        <option value="z1d.6xlarge" />
		        <option value="z1d.12xlarge" />
		        <option value="z1d.metal" />
		        <option value="i3.large" />
		        <option value="i3.xlarge" />
		        <option value="i3.2xlarge" />
		        <option value="i3.4xlarge" />
		        <option value="i3.8xlarge" />
		        <option value="i3.16xlarge" />
		        <option value="i3.metal" />
		        <option value="i4i.large" />
		        <option value="i4i.xlarge" />
		        <option value="i4i.2xlarge" />
		        <option value="i4i.4xlarge" />
		        <option value="i4i.8xlarge" />
		        <option value="i4i.16xlarge" />
		        <option value="i4i.metal" />
		        <option value="d2.xlarge" />
		        <option value="d2.2xlarge" />
		        <option value="d2.4xlarge" />
		        <option value="d2.8xlarge" />
		        <option value="d3.xlarge" />
		        <option value="d3.2xlarge" />
		        <option value="d3.4xlarge" />
		        <option value="d3.8xlarge" />
		        <option value="p3.2xlarge" />
		        <option value="p3.8xlarge" />
		        <option value="p3.16xlarge" />
		        <option value="p4d.24xlarge" />
		        <option value="g4dn.xlarge" />
		        <option value="g4dn.2xlarge" />
		        <option value="g4dn.4xlarge" />
		        <option value="g4dn.8xlarge" />
		        <option value="g4dn.12xlarge" />
		        <option value="g4dn.16xlarge" />
		      </datalist>
		    </div>

		  </div>
		)}

        <button onClick={handleLaunch} disabled={loading} style={styles.btn('#9333ea')}>
          {loading ? 'Launching…' : 'Launch'}
        </button>
        {message && (
          <p style={{ marginTop:8, color: /^Failed|^Error/.test(message) ? '#f44336' : '#4caf50' }}>
            {message}
          </p>
        )}
        {/* MinimalDashboard solo en modo Studio; EventWizard muestra la vista avanzada */}
        {studioMode ? (
          <MinimalDashboard
			onUploadTemplateMpd={async (evt, list, master) => {
              try {
                if (!master) throw new Error('Master instance not found.');

                // Prefer event times from DB; fallback to "now" and now-10s
                const now = new Date();
                let publish = eventPublishTimes[evt] || now.toISOString();
                let avail   = eventStartTimes[evt]   || new Date(now.getTime() - 10000).toISOString();
                try {
                  const { data } = await api.getEvent(evt);
                  if (data?.publishTime) publish = data.publishTime;
                  if (data?.availabilityStartTime) avail = data.availabilityStartTime;
                } catch {}

                const xml = buildTemplateMpd(evt, publish, avail);
                const safe = sanitizeMpd(xml, evt);

                // Save in DB (optional but nice to have)
                try { await api.saveMpd(evt, safe); } catch {}
                // Upload to master node path: /public/{evt}/master/manifest.mpd
                await uploadMpdToNode(evt, safe, master);
                setMessage(`Template MPD uploaded for "${evt}".`);
				await saveAndPushMpd(evt, safe, master);
				setMessage(`Template MPD uploaded (server) and saved to DB for "${evt}".`);
              } catch (e) {
                setMessage(`Template MPD upload failed: ${e.message}`);
              }
            }}
			grouped={grouped}
            rtmpOk={rtmpOk}
            ingestOk={ingestOk}
            ingestKbps={ingestKbps}
            ffmpegStat={ffmpegStat}
            speedMap={speedMap}
            sourceOverrideMap={sourceOverrideMap}
            onRefresh={() => fetchFullRef.current()}
            setMessage={setMessage}
            onGenerateAndUploadMpd={handleGenerateAndUpload}
            api={api}
            pendingLaunchesByEvent={pendingLaunchesByEvent}
            presets={presets}
            manifestText={manifestText}
            manifestMeta={manifestMeta}
            showManifest={showManifest}
            setShowManifest={setShowManifest}
            fetchManifestForEvent={fetchManifestForEvent}
            cmdFromDb={cmdFromDb}
            dashSettingsContent={dashSettingsContent}
            setDashSettingsContent={setDashSettingsContent}
            saveDashSettings={api.saveDashSettings}
          />
        ) : (
          <MinimalDashboard
            grouped={grouped}
            rtmpOk={rtmpOk}
            ingestOk={ingestOk}
            ingestKbps={ingestKbps}
            ffmpegStat={ffmpegStat}
            speedMap={speedMap}
            sourceOverrideMap={sourceOverrideMap}
            onRefresh={() => fetchFullRef.current()}
            setMessage={setMessage}
            onGenerateAndUploadMpd={handleGenerateAndUpload}
            api={api}
            pendingLaunchesByEvent={pendingLaunchesByEvent}
            presets={presets}
            manifestText={manifestText}
            manifestMeta={manifestMeta}
            showManifest={showManifest}
            setShowManifest={setShowManifest}
            fetchManifestForEvent={fetchManifestForEvent}
            advancedMode={true}
            cmdFromDb={cmdFromDb}
            onPresetsReload={reloadPresets}
            dashSettingsContent={dashSettingsContent}
            setDashSettingsContent={setDashSettingsContent}
            saveDashSettings={api.saveDashSettings}
            fetchFull={() => fetchFullRef.current()}
            backendUrl={backendUrl}
            manifestText8K={manifestText8K}
            manifestTextFull={manifestTextFull}
            setManifestText={setManifestText}
            setManifestText8K={setManifestText8K}
            setManifestTextFull={setManifestTextFull}
            saveAndPushMpd={saveAndPushMpd}
          />
        )}
      </div>

      {/* FFmpeg Presets Panel Modal */}
      {showPresetsPanel && (
        <FfmpegPresetsPanel
          api={{
            listPresets: api.listPresets,
            createPreset: api.createPreset,
            updatePreset: api.updatePreset,
            deletePreset: api.deletePreset
          }}
          onClose={() => {
            setShowPresetsPanel(false);
            reloadPresets(); // Refresh presets list after closing
          }}
          setMessage={setMessage}
        />
      )}
    </div>
  );
}
